import { z } from 'zod'
import { uscMultiply } from '../../common'
import { guidSchema, uscSchema } from '../../contracts/_common'
import { PriceEngineOptions } from '../../contracts/PriceEngineOptions'
import { Guid } from '../common-schemas'
import { InvoiceCartItem } from '../InvoicesV2/InvoicesV2'
import { JobClass } from '../Job'
import { MaintenancePlanDiscount, MaintenancePlanStatus } from './MaintenancePlanTypes'

// Hardcoded minimum threshold - $1 in cents
const MINIMUM_SAVINGS_THRESHOLD_USC = 100

export const MaintenancePlanSavingsEligibilitySchema = z.discriminatedUnion('eligible', [
  z.object({
    eligible: z.literal(false),
  }),
  z.object({
    eligible: z.literal(true),
    potentialSavingsUsc: uscSchema,
    planDefinitionGuid: guidSchema,
    planName: z.string(),
  }),
])

export type MaintenancePlanSavingsEligibility = z.infer<typeof MaintenancePlanSavingsEligibilitySchema>

export interface MaintenancePlanDefinitionInfo {
  maintenancePlanDefinitionGuid: Guid
  name: string
  discounts: MaintenancePlanDiscount[]
  memberPricingEnabled: boolean
}

export interface CompanyPricingTierConfig {
  memberTierEnabled: boolean
}

export interface LineItemPricebookItem {
  pricebookItemGuid: Guid
  basePriceUsc: number
  memberPriceUsc: number
}

export interface LineItemWithPricing extends InvoiceCartItem {
  pricebookItem?: LineItemPricebookItem
}

export interface CalculateMaintenancePlanSavingsParams {
  lineItems: LineItemWithPricing[]
  taxRate: number
  jobClass: JobClass
  hasActiveMaintenancePlan: boolean
  maintenancePlanStatus?: MaintenancePlanStatus
  availablePlans: MaintenancePlanDefinitionInfo[]
  companyPricingTierConfig: CompanyPricingTierConfig
  priceEngineOptions: PriceEngineOptions
}

/**
 * Calculate potential maintenance plan savings for a customer without an active plan.
 * Returns the best available savings from either discount-based or member pricing.
 */
export function calculateMaintenancePlanSavings(
  params: CalculateMaintenancePlanSavingsParams,
): MaintenancePlanSavingsEligibility {
  const {
    lineItems,
    jobClass,
    hasActiveMaintenancePlan,
    maintenancePlanStatus,
    availablePlans,
    companyPricingTierConfig,
  } = params

  // Early return if customer already has an active maintenance plan
  if (hasActiveMaintenancePlan) {
    return { eligible: false }
  }

  // Treat expired/canceled plans as non-members
  if (
    maintenancePlanStatus === MaintenancePlanStatus.ACTIVE ||
    maintenancePlanStatus === MaintenancePlanStatus.PENDING
  ) {
    return { eligible: false }
  }

  // Early return if no plans available
  if (availablePlans.length === 0) {
    return { eligible: false }
  }

  let bestSavingsUsc = 0
  let bestPlan: MaintenancePlanDefinitionInfo | undefined

  // Check each available plan for potential savings
  for (const plan of availablePlans) {
    let planSavingsUsc = 0

    // Calculate discount-based savings for this job class
    let discountableSubtotalUsc = 0
    for (const lineItem of lineItems) {
      if (lineItem.isDiscountable) {
        const lineItemTotalUsc = uscMultiply(lineItem.unitPriceUsc, lineItem.quantity)
        discountableSubtotalUsc += lineItemTotalUsc
      }
    }

    const discountForJobClass = plan.discounts.find(d => d.discountJobClass === jobClass)
    if (discountForJobClass && discountForJobClass.discountRate > 0 && discountableSubtotalUsc > 0) {
      const discountSavingsUsc = Math.ceil(discountableSubtotalUsc * discountForJobClass.discountRate)
      planSavingsUsc = Math.max(planSavingsUsc, discountSavingsUsc)
    }

    // Calculate member pricing savings if enabled for both company and plan
    if (plan.memberPricingEnabled && companyPricingTierConfig.memberTierEnabled) {
      let memberPricingSavingsUsc = 0

      // Calculate savings from tiered pricing (difference between base price and member price)
      for (const lineItem of lineItems) {
        if (lineItem.isDiscountable && lineItem.pricebookItem) {
          const basePriceUsc = lineItem.pricebookItem.basePriceUsc
          const memberPriceUsc = lineItem.pricebookItem.memberPriceUsc

          // Calculate savings per unit
          const savingsPerUnitUsc = basePriceUsc - memberPriceUsc

          // Only count positive savings
          if (savingsPerUnitUsc > 0) {
            const totalSavingsForLineItemUsc = uscMultiply(savingsPerUnitUsc, lineItem.quantity)
            memberPricingSavingsUsc += totalSavingsForLineItemUsc
          }
        }
      }

      planSavingsUsc = Math.max(planSavingsUsc, memberPricingSavingsUsc)
    }

    // Track the best savings across all plans
    if (planSavingsUsc > bestSavingsUsc) {
      bestSavingsUsc = planSavingsUsc
      bestPlan = plan
    }
  }

  // Apply minimum threshold check
  if (bestSavingsUsc < MINIMUM_SAVINGS_THRESHOLD_USC || !bestPlan) {
    return { eligible: false }
  }

  return {
    eligible: true,
    potentialSavingsUsc: bestSavingsUsc,
    planDefinitionGuid: bestPlan.maintenancePlanDefinitionGuid,
    planName: bestPlan.name,
  }
}

/**
 * Format savings amount for display
 */
export function formatSavingsAmount(savingsUsc: number): string {
  const dollars = Math.floor(savingsUsc / 100)
  const cents = savingsUsc % 100

  if (cents === 0) {
    return `$${dollars}`
  }

  return `$${dollars}.${cents.toString().padStart(2, '0')}`
}

/**
 * Check if maintenance plan savings feature is enabled for a company
 */
export interface MaintenancePlanSavingsConfig {
  maintenancePlanSavingsEnabled: boolean
  maintenancePlanSavingsCtaUrl?: string | null
}

export function isMaintenancePlanSavingsEnabled(config: MaintenancePlanSavingsConfig): boolean {
  return config.maintenancePlanSavingsEnabled === true
}

/**
 * Get the CTA URL for maintenance plan signup
 */
export function getMaintenancePlanCtaUrl(
  config: MaintenancePlanSavingsConfig,
  defaultUrl: string = '/maintenance-plans',
): string {
  return config.maintenancePlanSavingsCtaUrl || defaultUrl
}
